import React, { useRef, useState } from "react";
import { createStyles, makeStyles, TextField, Theme } from "@material-ui/core";
import { observer } from "mobx-react";
import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@material-ui/lab";
import REMAutocomplete from "../../../shared/modules/rem-conversion/components/Autocomplete";
import REMChip from "../../../shared/modules/rem-conversion/components/Chip";
import { isValidEmail } from "../../../logic/ValidationChecks/Validation";
import { INotifiable } from "../../../stores/models/interfaces/INotifiable";
import clsx, { ClassValue } from "clsx";
import { ClipboardText } from "mdi-material-ui";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        highlight: {
            background: theme.palette.primaryBackground.main
        }
    })
);

export interface ILeadOption {
    id: number;
    email: string;
}

interface LeadsAutocompleteProps {
    options: INotifiable[];
    highlightedOptions?: INotifiable[];
    selections: INotifiable[];
    onChange: (
        event: React.ChangeEvent<{}>,
        value: (string | INotifiable)[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string | INotifiable> | undefined
    ) => void
}

const LeadsAutocomplete = observer((props: LeadsAutocompleteProps) => {

    const classes = useStyles();

    /********* State *********/

    const [inputValue, setInputValue] = useState('');
    const [error, setError] = useState(false);

    const inputValueRef = useRef('');

    /********* Event handlers *********/

    const onSelectionsChanged = (
        event: React.ChangeEvent<{}>,
        value: (string | INotifiable)[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<string | INotifiable> | undefined
    ) => {

        if (reason === 'create-option') {
            const lastValue = value[value.length - 1];

            if (typeof lastValue === 'string' && !isValidEmail(lastValue)) {
                setError(true);

                // Don't update value, so no invalid chip is added.
                return;
            }

            const valueAlreadyEntered = props.selections.findIndex(selectedOption => selectedOption.email === lastValue) !== -1;
            if (valueAlreadyEntered) {
                return
            }
        }

        if (reason === 'select-option') {
            const newInputValue = '';
            setInputValue(newInputValue);
            inputValueRef.current = newInputValue;
        }

        setError(false);

        const normalizedDetails = details
            ? {
                ...details,
                option: typeof details.option === "string"
                    ? details.option.trim().toLowerCase()
                    : {
                        ...details.option,
                        email: details.option.email.trim().toLowerCase()
                    }
            }
            : undefined;

        props.onChange(
            event,
            value,
            reason,
            normalizedDetails
        )
    };

    /********* Render *********/

    return (
        <REMAutocomplete
            inputValue={inputValue}
            multiple
            options={props.options}
            getOptionLabel={(option) => typeof option === "string" ? option : option.email}
            getOptionSelected={(option, value) => {
                const optionEmail = typeof option === "string" ? option : option.email;
                const valueEmail = typeof value === "string" ? value : value.email;
                return optionEmail === valueEmail;
            }}
            freeSolo
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const shouldHighlight = props.highlightedOptions && props.highlightedOptions.findIndex(highlightedOption => typeof option === 'object' && highlightedOption.email === option.email) !== -1;
                    const chipProps = getTagProps({ index });
                    return (
                        <REMChip
                            variant="outlined"
                            // label={typeof option === "string" ? option : 'id' in option ? option.email + " id: " + option.id : option.email}
                            label={typeof option === "string" ? option : option.email}
                            {...chipProps}
                            icon={shouldHighlight ? <ClipboardText /> : undefined}
                            className={shouldHighlight
                                ? 'className' in chipProps
                                    ? clsx(classes.highlight, chipProps.className as ClassValue)
                                    : classes.highlight
                                : 'className' in chipProps
                                    ? chipProps.className as string
                                    : undefined}
                        />
                    )
                })
            }
            filterSelectedOptions
            openOnFocus
            autoHighlight
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    error={error}
                    helperText={error ? 'Enter a valid email address' : ''}
                    inputProps={{
                        ...params.inputProps,
                        onKeyDown: (event) => {
                            // Prevent form submission upon hitting Enter with no text entry:
                            if (event.key === 'Enter' && inputValue.trim() === '') {
                                event.preventDefault();
                                event.stopPropagation();
                            }

                            // Proceed with the other key down handling
                            if ('onKeyDown' in params.inputProps && typeof params.inputProps.onKeyDown === "function") {
                                params.inputProps.onKeyDown?.(event);
                            }
                        }
                    }}
                />
            )}
            value={props.selections}
            onChange={onSelectionsChanged}
            onInputChange={(event, newInputValue, reason) => {

                if (reason === 'input') {
                    inputValueRef.current = newInputValue;
                }

                if (
                    reason === 'reset' &&
                    inputValueRef.current !== '' &&
                    !isValidEmail(inputValueRef.current)
                ) {
                    setInputValue(inputValueRef.current); // Maintain the input entry if the entered text is not a valid email
                    return;
                }

                setInputValue(newInputValue);

                // Check whether or not an existing error should be cleared
                if (error === true) {
                    if (newInputValue === '' || isValidEmail(newInputValue)) {
                        setError(false);
                    }
                }
            }}
        />
    );
});

export default LeadsAutocomplete;